/**
 * @file SettingsGroups.tsx
 * @description Корень панели из групп настроек (phase19.16, SB122;
 * uiux-settings-groups §1–§2, §8): держит состояние групп
 * (`useSettingsGroups`) и отдаёт его группам через контекст; сверху — одна
 * липкая строка `PanelStickyTop`: свой верх панели (`stickyTop`, например
 * «← К элементу») и оглавление `SettingsToc` (от 4 групп и от 768).
 *
 * ```tsx
 * <SettingsGroups panel="footer" defaultCollapsed={["always"]}>
 *   <SettingsGroup id="layout" title="Расположение" summary="Колонками">…</SettingsGroup>
 * </SettingsGroups>
 * ```
 */

import type { CSSProperties, ReactNode } from "react";

import { SettingsGroupsContext } from "./context";
import { PanelStickyTop } from "./PanelStickyTop";
import { SettingsToc } from "./SettingsToc";
import { useSettingsGroups } from "./useSettingsGroups";
import { cn } from "@/lib/utils";

/** С какого числа групп показывать оглавление-пилюли. */
export const TOC_MIN_GROUPS = 4;

interface Props {
  /** Ключ памяти: `commora.settings-groups.<panel>`. */
  panel: string;
  /** Группы, свёрнутые по умолчанию на ПК (редкие и «только просмотр»). */
  defaultCollapsed?: readonly string[];
  /** Своё содержимое липкой строки над оглавлением (ссылка возврата). */
  stickyTop?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function SettingsGroups({ panel, defaultCollapsed, stickyTop, children, className }: Props) {
  const api = useSettingsGroups({ panel, defaultCollapsed });
  const showToc = !api.narrow && api.groups.length >= TOC_MIN_GROUPS;
  const hasSticky = showToc || (stickyTop !== undefined && stickyTop !== null);
  const style = { "--panel-sticky-h": `${hasSticky ? api.stickyHeight : 0}px` } as CSSProperties;

  return (
    <SettingsGroupsContext.Provider value={api}>
      <div ref={api.rootRef} data-slot="settings-groups" style={style} className={cn("flex min-w-0 flex-col gap-4", className)}>
        {hasSticky ? (
          <PanelStickyTop onHeight={api.setStickyHeight}>
            {stickyTop}
            {showToc ? <SettingsToc api={api} /> : null}
          </PanelStickyTop>
        ) : null}
        {children}
      </div>
    </SettingsGroupsContext.Provider>
  );
}
